import { useEffect, useState } from "react";
import { Button, DialogActions, DialogContent, DialogTitle, Grid, TextField } from "@mui/material";
import { createEntityStore, PropValidator } from "../../common/entityStore";
import { propBoolField, propCustomEnumField, propEnumField, propMultiEnumField, propNumberField, propSimpleEnumField, propTextField } from "../../common/entityStoreForm";
import { renderClose, useDialog } from "../../common/dialog";
import { propCurrency, propInt, propLength, propMaxValue, propMinValue, propMinValueExclusive, propNumber, propRequired, propValidValue, propValueValidator } from "../../common/entityStoreValidators";
import { createListStore, UseListStore } from "../../common/listStore";
import { EnumItemDto, MailProductCode, PaymentMethodId, useDeliveryMethods, usePaymentMethods } from "../../common/enum";
import { getLanguages, getLanguagesRaw } from "../../common/utils";
import { useDefaultValues } from "../../common/defaultValues";
import { ProductDto, ProjectDto } from "./common";
import { map } from "rxjs";
import { ProjectMailOrderConditionListDialog } from "./ProjectMailOrderConditionListDialog";
import { SaleArticleDetailDto, SaleArticleDto } from "../../common/order";
import { EntityStoreSaveButton } from "../../common/SaveButton";
import { TmSmsTemplateListDialog } from "./TmSmsTemplateListDialog";

export const generatePostNrOptions = [
    { value: null, label: "Use delivery method settings" },
    { value: true, label: "Yes" },
    { value: false, label: "No" },
];

export function ProjectDialog(props: {
    close: () => void;
    product: ProductDto;
    project: ProjectDto | null,
    useProjectListStore: UseListStore<ProjectDto>;
    useSaleArticleListStore: UseListStore<SaleArticleDto>;
    useProjectSaleArticleListStore: UseListStore<SaleArticleDetailDto>;
    projectChanged(project: ProjectDto): void;
    copyFromProject?: ProjectDto;
    setCopyFromProject?(project: ProjectDto): void;
}) {
    const [useProjectStore] = useState(() => createEntityStore<ProjectEditDto>({
        path: "products/projects",
        saveProps: [
            "productId", "code", "name", "defaultShipmentUnit", "maxDiscount", "isNew", "paymentMethodId", "language",
            "shipmentLetters", "combineStatementLetters", "welcomePackageLetters", "offerLetters", "offerCsLetters",
            "quickResponsePresentLetters", "qrCsLetters", "mailOrderShipmentLetters", "sendWelcomePackage", "welcomePackageDiscount",
            "giftShipment", "freeArticleId", "giftArticleId", "freeRegularShipmentAfterPaid",
            "deliveryMethodId", "maxDiscountAmount", "minOrderAmount", "paymentMethodIds", "generatePostNr",
            "copyFromProjectId", "baseProjectId",
        ],
        id: props.project != null ? props.project.id : null,
        getNewId: p => p.id!,
        data: props.project != null ? {
            ...props.project,
            maxDiscountFormatted: undefined,
            giftShipmentFormatted: undefined,
            welcomePackageDiscountFormatted: undefined,
            defaultShipmentUnitFormatted: undefined,
            freeRegularShipmentAfterPaidFormatted: undefined,
            maxDiscountAmountFormatted: undefined,
            minOrderAmountFormatted: undefined,
        } : newProject(props.product, props.useProjectListStore, props.copyFromProject),
        fetchOnInit: false,
        onSaved(project) {
            props.projectChanged(project as ProjectDto);
            props.close();
        },
    }));
    const [useFreeArticlesStore] = useState(() => createListStore<EnumItemDto>({
        data: props.useSaleArticleListStore.getState().items$.pipe(map(p => ({
            items: p.items.map(p => ({
                id: p.id,
                name: `${p.code} ${p.name}`
            })),
            isFetching: p.isFetching
        })))
    }));
    const [useQrpArticlesStore] = useState(() => createListStore<EnumItemDto>({ data: useFreeArticlesStore.getState().items$ }));
    var projectStore = useProjectStore(p => ({
        serverData: p.serverData,
        entityId: p.entityId,
        isClientDataSame: p.isClientDataSame,
    }));

    const allProjects = props.useProjectListStore(p => p.items);
    const isMo = props.product.code === MailProductCode;
    useEffect(() => {
        useProjectStore.getState().setPropValidators([
            propRequired("code"),
            propLength("code", 4, 4),
            propValueValidator("code", (p, e) =>
                allProjects.some(r => r.code === p && (e.id == null || e.id !== r.id)) &&
                "Project code must be unique."),

            propRequired("name"),
            propLength("name", 0, 50),

            propRequired("paymentMethodId"),
            propRequired("language"),
            propValueValidator("paymentMethodId", (p, e) => e.paymentMethodIds.length > 0 && !e.paymentMethodIds.includes(p) && "Payment method not allowed."),

            ...(isMo ? [
                propRequired("mailOrderShipmentLetters"), propLength("mailOrderShipmentLetters", 1, 15),

                propValidValue("maxDiscountAmount", "maxDiscountAmountFormatted"),
                propMinValue("maxDiscountAmount", 0),
                propCurrency("maxDiscountAmount"),

                propValidValue("minOrderAmount", "minOrderAmountFormatted"),
                propMinValueExclusive("minOrderAmount", 0),
                propCurrency("minOrderAmount"),

            ] as PropValidator<ProjectEditDto>[] : [
                propValidValue("defaultShipmentUnit", "defaultShipmentUnitFormatted"),
                propRequired("defaultShipmentUnit"),
                propMinValue("defaultShipmentUnit", 1),
                propMaxValue("defaultShipmentUnit", props.product.maxShipmentUnit),
                propInt("defaultShipmentUnit"),

                propValidValue("maxDiscount", "maxDiscountFormatted"),
                propRequired("maxDiscount"),
                propMinValue("maxDiscount", 0),
                propMaxValue("maxDiscount", 100),
                propInt("maxDiscount"),

                propRequired("shipmentLetters"), propLength("shipmentLetters", 1, 15),
                propRequired("welcomePackageLetters"), propLength("welcomePackageLetters", 1, 15),
                propRequired("combineStatementLetters"), propLength("combineStatementLetters", 1, 15),
                propRequired("offerLetters"), propLength("offerLetters", 1, 15),
                propRequired("offerCsLetters"), propLength("offerCsLetters", 1, 15),
                propRequired("quickResponsePresentLetters"), propLength("quickResponsePresentLetters", 1, 15),
                propRequired("qrCsLetters"), propLength("qrCsLetters", 1, 15),

                propValidValue("welcomePackageDiscount", "welcomePackageDiscountFormatted"),
                propRequired("welcomePackageDiscount"),
                propNumber("welcomePackageDiscount", p => (p < 0) && `Value must be 0 or more.`),
                propCurrency("welcomePackageDiscount"),

                propValidValue("freeRegularShipmentAfterPaid", "freeRegularShipmentAfterPaidFormatted"),
                propRequired("freeRegularShipmentAfterPaid"),
                propNumber("freeRegularShipmentAfterPaid", p => (p < 0) && `Value must be 0 or more.`),
                propInt("freeRegularShipmentAfterPaid"),

                propValidValue("giftShipment", "giftShipmentFormatted"),
                propRequired("giftShipment"),
                propNumber("giftShipment", p => (p < 1) && `Value must be 1 or more.`),
                propInt("giftShipment"),
            ] as PropValidator<ProjectEditDto>[]),
        ]);
    }, [allProjects, useProjectStore, props.product, isMo]);

    const def = useDefaultValues(p => p.clientData);
    useEffect(() => {
        const languages = getLanguagesRaw(def);
        const defaultLanguage = languages[0];
        if (!useProjectStore.getState().clientData.language && defaultLanguage) {
            useProjectStore.getState().setClientDataProp("language", defaultLanguage);
        }
    }, [def, useProjectStore]);
    const languages = getLanguages(def);
    const isNew = projectStore.entityId == null;

    const moConditionsDialog = useDialog(close => <ProjectMailOrderConditionListDialog close={close} product={props.product!} project={props.project!}
        useProjectSaleArticleListStore={props.useProjectSaleArticleListStore} />, "xl");
    const tmSmsTemplateListDialog = useDialog((close, dialogProps) => <TmSmsTemplateListDialog close={close} dialogProps={dialogProps}
        product={props.product!} project={props.project!} />, undefined, { usingCustomDialogTag: true });

    return <>
        <DialogTitle>{projectStore.entityId == null ? "New project" :
            `Project ${projectStore.serverData.code} ${projectStore.serverData.name}`}</DialogTitle>
        {renderClose(props.close)}
        {moConditionsDialog.render()}
        {tmSmsTemplateListDialog.render()}
        <DialogContent>
            <Grid container columnSpacing={2}>
                <Grid item xs={12}><TextField label="Product" value={props.product.code + " " + props.product.name} disabled /></Grid>
                <Grid item xs={4}>{propTextField(useProjectStore)("code", "Code", { disabled: !isNew })}</Grid>
                {!isMo && <Grid item xs={4}>{propNumberField(useProjectStore)("defaultShipmentUnit", "defaultShipmentUnitFormatted", "Default SU")}</Grid>}
                {!isMo && <Grid item xs={4}>{propNumberField(useProjectStore)("maxDiscount", "maxDiscountFormatted", "Max discount", { unit: "%" })}</Grid>}
                {isMo && <Grid item xs={4}>{propNumberField(useProjectStore)("maxDiscountAmount", "maxDiscountAmountFormatted", "Discount", { isCurrency: true })}</Grid>}
                {isMo && <Grid item xs={4}>{propNumberField(useProjectStore)("minOrderAmount", "minOrderAmountFormatted", "Min order amount", { isCurrency: true })}</Grid>}
                <Grid item xs={12}>{propTextField(useProjectStore)("name", "Description")}</Grid>
                <Grid item xs={12}>{propBoolField(useProjectStore)("isNew", "Show in project list for new manual orders?")}</Grid>
                <Grid item xs={6}>{propEnumField(useProjectStore)("paymentMethodId", "Default payment method", usePaymentMethods)}</Grid>
                <Grid item xs={6}>{propMultiEnumField(useProjectStore)("paymentMethodIds", "Allowed payment methods", usePaymentMethods, { emptyLabel: "All" })}</Grid>
                <Grid item xs={6}>{propEnumField(useProjectStore)("deliveryMethodId", "Delivery method", useDeliveryMethods, { displayEmpty: true, addNone: true, noneText: "Use product settings" })}</Grid>
                <Grid item xs={6}>{propSimpleEnumField(useProjectStore)("language", "Language", languages)}</Grid>
                <Grid item xs={6}>{propTextField(useProjectStore)("shipmentLetters", "Shp letter", { disabled: isMo })}</Grid>
                <Grid item xs={6}>{propTextField(useProjectStore)("welcomePackageLetters", "WP letter", { disabled: isMo })}</Grid>
                <Grid item xs={6}>{propTextField(useProjectStore)("combineStatementLetters", "C/S letter", { disabled: isMo })}</Grid>
                <Grid item xs={6}>{propTextField(useProjectStore)("mailOrderShipmentLetters", "MO letter", { disabled: !isMo })}</Grid>
                <Grid item xs={6}>{propTextField(useProjectStore)("offerLetters", "Offer letter", { disabled: isMo })}</Grid>
                <Grid item xs={6}>{propTextField(useProjectStore)("offerCsLetters", "Offer CS letter", { disabled: isMo })}</Grid>
                <Grid item xs={6}>{propTextField(useProjectStore)("quickResponsePresentLetters", "QRP letter", { disabled: isMo })}</Grid>
                <Grid item xs={6}>{propTextField(useProjectStore)("qrCsLetters", "QRP CS letter", { disabled: isMo })}</Grid>
                <Grid item xs={12}>{propEnumField(useProjectStore)("freeArticleId", "Free article", useFreeArticlesStore, { disabled: isMo, addNone: true })}</Grid>
                <Grid item xs={12}>{propBoolField(useProjectStore)("sendWelcomePackage", "Welcome pack?")}</Grid>
                <Grid item xs={6}>{propNumberField(useProjectStore)("welcomePackageDiscount", "welcomePackageDiscountFormatted", "WP discount", { disabled: isMo, isCurrency: true })}</Grid>
                <Grid item xs={6}>{propNumberField(useProjectStore)("freeRegularShipmentAfterPaid", "freeRegularShipmentAfterPaidFormatted", "Free RS after # paid shipments", { disabled: isMo })}</Grid>
                <Grid item xs={8}>{propEnumField(useProjectStore)("giftArticleId", "QRP article", useQrpArticlesStore, { disabled: isMo, addNone: true })}</Grid>
                <Grid item xs={4}>{propNumberField(useProjectStore)("giftShipment", "giftShipmentFormatted", "QRP shipment", { disabled: isMo })}</Grid>
                <Grid item xs={6}>{propBoolField(useProjectStore)("generatePostNr", "Generate postal number", { enumValues: generatePostNrOptions })}</Grid>
                <Grid item xs={6}>{propCustomEnumField(useProjectStore)("baseProjectId", "Base project", props.useProjectListStore, { autoComplete: true, getId: p => p.id, getName: p => `${p.code} ${p.name}`, filter: p => p.id !== props.project?.id })}</Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => tmSmsTemplateListDialog.show()} disabled={projectStore.entityId == null}>TM SMS</Button>
            {props.project != null && props.setCopyFromProject && <Button disabled={!projectStore.isClientDataSame} onClick={() => { props.setCopyFromProject!(props.project!); props.close(); }}>Copy</Button>}
            {props.product.code === MailProductCode && <Button onClick={() => moConditionsDialog.show()} disabled={projectStore.entityId == null}>MO conditions</Button>}
            <EntityStoreSaveButton useStore={useProjectStore} />
            <Button onClick={props.close} color="negative" variant="outlined">Close</Button>
        </DialogActions>
    </>;
}

export type ProjectEditDto = Omit<ProjectDto, "id"> & {
    id: number | null;
    defaultShipmentUnitFormatted?: string;
    maxDiscountFormatted?: string;
    giftShipmentFormatted?: string;
    welcomePackageDiscountFormatted?: string;
    freeRegularShipmentAfterPaidFormatted?: string;
    maxDiscountAmountFormatted?: string;
    minOrderAmountFormatted?: string;
    copyFromProjectId?: number | null;
};

function newProject(product: ProductDto, useProjectListStore: UseListStore<ProjectDto>, copyFromProject: ProjectDto | undefined): ProjectEditDto {
    const dp = useProjectListStore.getState().items.find(p => p.isDefault);
    return {
        id: null,
        productId: product.id,
        isDefault: false,
        code: copyFromProject?.code ?? "",
        name: copyFromProject?.name ?? "",
        defaultShipmentUnit: copyFromProject?.defaultShipmentUnit ?? 1,
        maxDiscount: copyFromProject?.maxDiscount ?? 25,
        maxDiscountAmount: copyFromProject?.maxDiscountAmount,
        minOrderAmount: copyFromProject?.minOrderAmount,
        isNew: copyFromProject?.isNew ?? false,
        freeRegularShipmentAfterPaid: copyFromProject?.freeRegularShipmentAfterPaid ?? 0,
        freeArticleId: copyFromProject?.freeArticleId ?? null,
        giftArticleId: copyFromProject?.giftArticleId ?? null,
        giftShipment: copyFromProject?.giftShipment ?? 1,
        language: copyFromProject?.language ?? "",
        deliveryMethodId: copyFromProject?.deliveryMethodId ?? null,

        shipmentLetters: copyFromProject?.shipmentLetters ?? dp?.shipmentLetters ?? "",
        combineStatementLetters: copyFromProject?.combineStatementLetters ?? dp?.combineStatementLetters ?? "",
        sendWelcomePackage: true,
        welcomePackageLetters: copyFromProject?.welcomePackageLetters ?? dp?.welcomePackageLetters ?? "",
        offerLetters: copyFromProject?.offerLetters ?? dp?.offerLetters ?? "",
        offerCsLetters: copyFromProject?.offerCsLetters ?? dp?.offerCsLetters ?? "",
        quickResponsePresentLetters: copyFromProject?.quickResponsePresentLetters ?? dp?.quickResponsePresentLetters ?? "",
        qrCsLetters: copyFromProject?.qrCsLetters ?? dp?.qrCsLetters ?? "",
        mailOrderShipmentLetters: copyFromProject?.mailOrderShipmentLetters ?? dp?.mailOrderShipmentLetters ?? "",
        paymentMethodId: copyFromProject?.paymentMethodId ?? dp?.paymentMethodId ?? PaymentMethodId.Cod,
        welcomePackageDiscount: copyFromProject?.welcomePackageDiscount ?? dp?.welcomePackageDiscount ?? 0,

        paymentMethodIds: copyFromProject?.paymentMethodIds ?? [],
        generatePostNr: copyFromProject?.generatePostNr ?? null,
        baseProjectId: copyFromProject?.id ?? null,
        copyFromProjectId: copyFromProject?.id ?? null,
    };
}
